/**
 * Ostinato Labs Theme
 *
 * Centralized brand colors and design tokens for the DAW UI.
 * All components should import colors from here for consistency.
 */

// Brand Color Palette
export const brand = {
  // Primary backgrounds
  charcoal: '#2b3135',
  charcoalLight: '#363d42',
  charcoalDark: '#1f2427',
  charcoalDeepest: '#111518',

  // Accent colors (Brand Guide: Forest Green)
  sage: '#A6B5A7',
  sageDark: '#8A9A7A',
  sageLight: '#BFD4C0',

  // Secondary accent (Brand Guide: Terracotta Brown)
  copper: '#E87856',
  copperDark: '#C45A3A',
  copperLight: '#F09878',

  // Text colors
  cream: '#e8e4df',
  creamMuted: '#a8a4a0',
  creamDim: '#6b6965',

  // Semantic colors
  error: '#fca5a5',
  errorBg: '#4a2020',
  success: '#86efac',
  successBg: '#1a4a2e',
  warning: '#fcd34d',
  warningBg: '#4a3b1a',
} as const;

// Spacing scale (rem-based)
export const spacing = {
  xs: '0.25rem',   // 4px
  sm: '0.5rem',    // 8px
  md: '1rem',      // 16px
  lg: '1.5rem',    // 24px
  xl: '2rem',      // 32px
  xxl: '3rem',     // 48px
} as const;

// Border radius
export const radius = {
  sm: '0.375rem',  // 6px - unified for landing/app consistency
  md: '0.5rem',    // 8px
  lg: '0.75rem',   // 12px
  full: '9999px',  // pill shape
} as const;

// Font sizes
export const fontSize = {
  xs: '0.625rem',  // 10px
  sm: '0.75rem',   // 12px
  md: '0.875rem',  // 14px
  lg: '1rem',      // 16px
  xl: '1.125rem',  // 18px
  xxl: '1.5rem',   // 24px
} as const;

// Font weights
export const fontWeight = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
  black: 900,
} as const;

// Z-index layers
export const zIndex = {
  base: 0,
  dropdown: 100,
  sticky: 200,
  modal: 300,
  tooltip: 400,
  toast: 500,
} as const;

// Transition durations
export const transition = {
  fast: '0.1s',
  normal: '0.2s',
  slow: '0.3s',
} as const;

// Panel layout constants
export const layout = {
  headerHeight: '48px',
  inputBarHeight: '64px',
  trackRowHeight: '60px',
  timelineHeight: '40px',
  minPanelWidth: '200px',
  defaultConversationWidth: '320px',
  defaultTrackListWidth: '120px',
} as const;

// Responsive breakpoints (mobile-first)
export const breakpoints = {
  xs: 0,      // Mobile portrait
  sm: 480,    // Mobile landscape
  md: 768,    // Tablet
  lg: 1024,   // Desktop
  xl: 1280,   // Large desktop
  xxl: 1536,  // Extra large
} as const;

// Media query strings for matchMedia API
export const mediaQueries = {
  mobile: `(max-width: ${breakpoints.md - 1}px)`,
  tablet: `(min-width: ${breakpoints.md}px) and (max-width: ${breakpoints.lg - 1}px)`,
  desktop: `(min-width: ${breakpoints.lg}px)`,
  touch: '(hover: none) and (pointer: coarse)',
  mouse: '(hover: hover) and (pointer: fine)',
} as const;

// Touch-optimized sizes (minimum 44px per Apple HIG / Material Design)
export const touchTargets = {
  minimum: '44px',    // iOS HIG minimum
  comfortable: '48px', // Material Design recommended
  large: '56px',      // Easy reach
} as const;

// Mobile-specific layout constants
export const mobileLayout = {
  headerHeight: '56px',       // Taller for touch
  bottomNavHeight: '64px',    // iOS safe area friendly
  inputBarHeight: '80px',     // Larger for thumb reach
  fabSize: '56px',            // Floating action button
  sidePadding: '16px',        // Screen edge padding
  cellSize: '80px',           // Larger grid cells for touch
} as const;

// Safe area insets for notched phones (iPhone X+, Dynamic Island, home indicator)
export const safeAreaInsets = {
  top: 'env(safe-area-inset-top, 0px)',
  bottom: 'env(safe-area-inset-bottom, 0px)',
  left: 'env(safe-area-inset-left, 0px)',
  right: 'env(safe-area-inset-right, 0px)',
} as const;

// Common style mixins (React.CSSProperties)
export const mixins = {
  flexCenter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as React.CSSProperties,

  flexBetween: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  } as React.CSSProperties,

  flexColumn: {
    display: 'flex',
    flexDirection: 'column',
  } as React.CSSProperties,

  absoluteFill: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  } as React.CSSProperties,

  truncate: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,

  buttonReset: {
    border: 'none',
    background: 'none',
    padding: 0,
    margin: 0,
    cursor: 'pointer',
    font: 'inherit',
    color: 'inherit',
  } as React.CSSProperties,

  inputReset: {
    border: 'none',
    outline: 'none',
    background: 'none',
    font: 'inherit',
  } as React.CSSProperties,
} as const;

// Helper to create hover/active variants
export function getInteractiveColors(base: string, hover: string, active: string) {
  return {
    base,
    hover,
    active,
  };
}

// Pre-defined button variants
export const buttonVariants = {
  primary: {
    background: brand.sage,
    color: brand.charcoal,
    hoverBackground: brand.sageDark,
  },
  secondary: {
    background: brand.charcoalLight,
    color: brand.cream,
    hoverBackground: brand.charcoal,
  },
  accent: {
    background: brand.copper,
    color: brand.charcoal,
    hoverBackground: brand.copperDark,
  },
  danger: {
    background: brand.errorBg,
    color: brand.error,
    hoverBackground: '#5a2525',
  },
  ghost: {
    background: 'transparent',
    color: brand.cream,
    hoverBackground: brand.charcoalLight,
  },
} as const;

// Export theme object for convenience
export const theme = {
  brand,
  spacing,
  radius,
  fontSize,
  fontWeight,
  zIndex,
  transition,
  layout,
  mixins,
  buttonVariants,
  breakpoints,
  mediaQueries,
  touchTargets,
  mobileLayout,
} as const;

export default theme;
